<template>
  <div>
    <div class="form_header">
      <div>
        <h6>{{ $l('form_title_doc') || '产品相关文档' }}</h6>
        <p>
          {{ $l('form_paragraph_doc') || '业务人员上传的关于该产品的相关文档' }}
        </p>
      </div>
      <div class="ml-5" v-if="!disabled">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="uploadDocVisible = true"
        >
          <b-icon-plus></b-icon-plus>
          {{ $l('btn_uploadDoc') || '文档上传' }}
        </b-button>
      </div>
    </div>
    <div class="form_content" :class="{ empty: isEmpty }">
      <div v-if="!isEmpty" class="row">
        <div
          class="col-md-6 col-xl-4 doc-card d-flex"
          v-for="(item, index) in list"
          :key="index"
        >
          <div class="doc-item p-2 flex-grow-1 d-flex flex-column">
            <div class="d-flex flex-grow-1">
              <div class="d-flex">
                <el-avatar
                  shape="square"
                  :size="40"
                  :src="item.fileIcon"
                  class="bg-transparent"
                />
              </div>
              <div class="flex-grow-1" style="width: calc(100% - 40px);">
                <div class="d-flex">
                  <div class="flex-grow-1 px-2 dic-name text-truncate">
                    {{ item.fileName }}
                  </div>
                  <div class="dic-size">{{ item.fileSize }}</div>
                </div>
                <div class="dic-desc px-2 mt-1 text-truncate">
                  {{ item.fileDesc }}
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-end mt-1">
              <div class="d-flex align-items-center">
                <el-avatar :size="24" :src="item.modifyByMemImgUrl">
                  <span>U</span>
                </el-avatar>
                <div class="ml-1 doc-modify">
                  <span>
                    {{ item.modifyByName }}
                  </span>
                  <span style="color: #8E8E8E;font-weight: 400;">At:</span>
                  <span>{{ item.modifyTime }}</span>
                </div>
              </div>
              <div class="doc-opts">
                <i
                  class="el-icon-delete tool-btn"
                  @click="del(item)"
                  v-if="!disabled"
                />
                <i
                  class="el-icon-edit tool-btn"
                  @click="edit(item)"
                  v-if="!disabled"
                />
                <i class="el-icon-view tool-btn" @click="view(item)" />
                <i class="el-icon-download tool-btn" @click="download(item)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isEmpty" class="empty-content">
        <img src="/image/static/img/nodata.png" />
        <p>{{ $l('INDEX.tips_no_data') || '暂无数据！' }}</p>
      </div>
    </div>

    <UploadDoc
      :visible.sync="uploadDocVisible"
      :proofingCode="proofingCode"
      @reload="getData"
    />

    <EditDoc
      :visible.sync="editDocOpts.visible"
      :data="editDocOpts.data"
      @reload="getData"
    />
  </div>
</template>

<script>
import 'dayjs/locale/zh-cn'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)
import { request } from '@/api/service'
import EditLoading from '@/mixins/edit-loading'
import UploadDoc from '../upload-doc'
import EditDoc from '../edit-doc'
export default {
  mixins: [EditLoading],
  components: { UploadDoc, EditDoc },
  props: {
    proofingCode: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      list: [],
      loading: false,
      fileIcons: {},
      uploadDocVisible: false,
      editDocOpts: { visible: false, data: {} },
    }
  },
  computed: {
    isEmpty() {
      return !this.list?.length
    },
  },
  async mounted() {
    await this.getFileIcons()
    this.getData()
  },
  methods: {
    async getFileIcons() {
      const res = await request({
        url: '/sys/common/dict/system',
        method: 'POST',
        data: { dictType: 'FileTypeIcon' },
      }).catch(() => null)

      const data = res?.body?.data || []
      this.fileIcons = Object.fromEntries(
        data.map(({ value, label }) => [value, label])
      )
    },
    getFileIcon(fileName) {
      const getFileType = (filename) => {
        if (!filename) return ''
        if (!filename.includes('.')) return ''
        return filename.split('.').pop()
      }

      const fileType = getFileType(fileName) || 'OTHER'
      return this.$osscdn(
        this.fileIcons[fileType.toUpperCase()] || this.fileIcons['OTHER']
      )
    },
    async getData() {
      if (!this.proofingCode) return
      this.loading = true
      const res = await this.$api
        .customerProofingDocListApi({ proofingCode: this.proofingCode })
        .catch(() => null)
      this.loading = false
      if (!res) return
      const data = res.body.data

      this.list = data.map((item) => {
        const { fileName, modifyTime } = item
        return {
          ...item,
          fileIcon: this.getFileIcon(fileName),
          modifyTime: dayjs(modifyTime)
            .locale('zh-cn')
            .fromNow(),
        }
      })
    },
    async del({ proofingDocCode }) {
      const confirm = await this.$confirm(
        this.$l('INDEX.tips_delete') || '是否要删除该记录？',
        this.$l('INDEX.label_completeDesc') || '确认信息',
        {
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .customerProofingDocDelApi({ proofingDocCode })
        .catch(() => null)
      this.loadingClose()

      if (!res) return
      const { status } = res
      if (status !== 200) {
        this.$message.error(this.$l('INDEX.tips_delete_no') || '删除失败！')
        return
      }

      this.$message({
        showClose: true,
        message: this.$l('INDEX.tips_delete_ok') || '删除成功！',
        type: 'success',
      })
      this.getData()
    },
    edit(doc) {
      this.editDocOpts = { visible: true, data: doc }
    },
    view({ fileCode }) {
      this.$api.SYS_FILEDOWNLOAD(fileCode, { isAutoDown: true, preview: true })
    },
    download({ fileCode }) {
      this.$api.SYS_FILEDOWNLOAD(fileCode)
    },
  },
}
</script>

<style lang="scss" scoped>
.form_header {
  display: flex;
  align-items: flex-end;
  h6 {
    color: #101010;
  }
  p {
    font-size: 14px;
    color: #8f97ad;
    margin-bottom: 0;
  }
}
.form_content.empty {
  border: 1px dashed #bbbbbb;
  border-radius: 5px;
  padding: 20px;
  margin-top: 10px;
}
.empty-content {
  text-align: center;
  flex: 1;
  p {
    font-size: 13px;
    color: #606266;
    margin-top: 10px;
    margin-bottom: 0;
  }
}

.row {
  margin-right: -5px;
  margin-left: -5px;
  .col-md-6.col-xl-4 {
    padding-right: 5px;
    padding-left: 5px;
  }
}

.doc-card {
  margin-top: 10px;
  .doc-item {
    border: 1px dashed #bbbbbb;
    border-radius: 5px;
    max-width: 100%;
    .dic-name {
      font-size: 14px;
      color: #007bff;
      word-break: break-all;
    }
    .dic-size {
      font-size: 14px;
      color: #6c757d;
      font-weight: 500;
      min-width: max-content;
    }
    .dic-desc {
      font-size: 12px;
      color: #8e8e8e;
      word-break: break-all;
    }
    .doc-modify {
      font-size: 12px;
      color: #6c757d;
      font-weight: 500;
      span {
        margin-right: 5px;
      }
    }
    .doc-opts {
      color: #bbbbbb;
      min-width: max-content;
    }
  }
}
</style>
